import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Pencil, History, Calendar } from "lucide-react";
import { User } from "../types";

interface EmployeeTableProps {
  employees: User[];
  onSort: (field: string) => void;
  sortField: string;
  sortDirection: "asc" | "desc";
}

const SortIcon = () => (
  <span className="ml-1 text-xs text-muted-foreground">↑↓</span>
);

export const EmployeeTable = ({
  employees,
  onSort,
  sortField,
  sortDirection,
}: EmployeeTableProps) => {
  const handleSort = (field: string) => {
    onSort(field);
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    const aValue =
      sortField === "name"
        ? `${a.firstName} ${a.lastName}`
        : ((a[sortField as keyof User] as string | undefined) ?? "");
    const bValue =
      sortField === "name"
        ? `${b.firstName} ${b.lastName}`
        : ((b[sortField as keyof User] as string | undefined) ?? "");

    if (sortDirection === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <div className="rounded-md border">
      <ResponsiveTable>
        <ResponsiveTableHeader>
          <ResponsiveTableRow>
            <ResponsiveTableHead
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => handleSort("name")}
            >
              Name <SortIcon />
            </ResponsiveTableHead>
            <ResponsiveTableHead
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => handleSort("email")}
            >
              Email <SortIcon />
            </ResponsiveTableHead>
            <ResponsiveTableHead
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => handleSort("phoneNumber")}
            >
              Phone <SortIcon />
            </ResponsiveTableHead>
            <ResponsiveTableHead className="text-right w-auto">
              Actions
            </ResponsiveTableHead>
          </ResponsiveTableRow>
        </ResponsiveTableHeader>
        <ResponsiveTableBody>
          {sortedEmployees.map((employee) => (
            <ResponsiveTableRow
              key={employee.id}
              mobileTitle={`${employee.firstName} ${employee.lastName}`}
            >
              <ResponsiveTableCell mobileLabel="Name" mobilePriority="high">
                {`${employee.firstName} ${employee.lastName}`}
              </ResponsiveTableCell>
              <ResponsiveTableCell mobileLabel="Email" mobilePriority="medium">
                {employee.email}
              </ResponsiveTableCell>
              <ResponsiveTableCell mobileLabel="Phone" mobilePriority="medium">
                {employee.phoneNumber}
              </ResponsiveTableCell>
              <ResponsiveTableCell>
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Link
                    to={`/admin/employees/${employee.id}/edit`}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </Link>
                  <Link
                    to={`/admin/employees/${employee.id}/events/past`}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      <History className="h-4 w-4 mr-1" />
                      Work History
                    </Button>
                  </Link>
                  <Link
                    to={`/admin/employees/${employee.id}/events/upcoming`}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Upcoming Events
                    </Button>
                  </Link>
                </div>
              </ResponsiveTableCell>
            </ResponsiveTableRow>
          ))}
        </ResponsiveTableBody>
      </ResponsiveTable>
    </div>
  );
};
